import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { API } from '../config/apiConfig';

function ChannelManage() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [channelId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [channelRes, videosRes] = await Promise.all([
        axios.get(API.channelById(channelId), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(API.channelVideos(channelId), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (channelRes.data.success) {
        setChannel(channelRes.data.channel);
      }

      if (videosRes.data.success) {
        setVideos(videosRes.data.videos || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load channel data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (videoId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        API.toggleVideoActive(videoId),
        { isActive: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        fetchData(); // Refresh videos
      }
    } catch (err) {
      console.error('Error toggling video:', err);
      alert(err.response?.data?.error || 'Failed to toggle video status');
    }
  };

  const handleDeleteClick = (video) => {
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      const response = await axios.delete(API.deleteVideo(videoToDelete.VideoID), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setDeleteDialogOpen(false);
        setVideoToDelete(null);
        fetchData(); // Refresh videos
      }
    } catch (err) {
      console.error('Error deleting video:', err);
      alert(err.response?.data?.error || 'Failed to delete video');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/dashboard/channels/${channelId}`)}
            sx={{ mb: 1 }}
          >
            Back to Channel
          </Button>
          <Typography variant="h4">
            {channel?.ChannelName} - Manage Videos
          </Typography>
        </Box>
        <IconButton onClick={fetchData} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {videos.length === 0 ? (
        <Alert severity="info">No videos in this channel yet</Alert>
      ) : (
        <Grid container spacing={2}>
          {videos.map((video) => {
            const isActive = video.IsActive === true || video.IsActive === 1;
            
            return (
              <Grid item xs={12} key={video.VideoID}>
                <Card elevation={2}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                    {/* Thumbnail */}
                    <CardMedia
                      component="img"
                      sx={{
                        width: { xs: '100%', sm: 200 },
                        height: { xs: 200, sm: 120 },
                        objectFit: 'cover',
                      }}
                      image={video.ThumbnailUrl || 'https://via.placeholder.com/320x180?text=No+Thumbnail'}
                      alt={video.Title}
                    />

                    {/* Content */}
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Box sx={{ flexGrow: 1, pr: 2 }}>
                          <Typography variant="h6" component="h3">
                            {video.Title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            <Chip
                              icon={<CategoryIcon />}
                              label={video.CategoryName}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={<VisibilityIcon />}
                              label={`${video.Views} views`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={isActive ? 'success' : 'error'}
                            />
                          </Box>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/videos/${video.VideoID}/edit`)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color={isActive ? 'warning' : 'success'}
                            startIcon={isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            onClick={() => handleToggleActive(video.VideoID, isActive)}
                          >
                            {isActive ? 'Deactivate' : 'Activate'}
                          </Button>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(video)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Video</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{videoToDelete?.Title}"?
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ChannelManage;

